/**
 * The rock stream's track (see STREAM, stream_plan.md): a closed, smooth
 * loop laid out once from the seed in the global layer, built from circles
 * joined by their tangents. A star on the route is a circle outside its
 * ring (the arc round it a sweep or a glance); between stars, bend circles
 * in the open make hairpins and S-bends; the straights between get a
 * narrows or a gorge now and then. The whole thing is described by arc
 * length: `streamPoint(s)` is where the track is `s` blocks along, and
 * `streamCoord(p)` is how far along and across a world point is.
 *
 * Rocks ride it at STREAM.speed (see rockMotion.ts, pathOrigin): a rock at
 * belt coordinate `u` is at `streamPoint(u + speed * t)`. The belt is cut
 * into segments of about STREAM.segment blocks (see segmentOf), each owned
 * by one simulation.
 */
import { STREAM } from "../constants";
import { clamp, v2, vDist, vLen, type Vec2 } from "../math";
import type { SphereConfig } from "./areas";
import { Rng } from "./rng";
import type { SunDef } from "./types";

/** A circle the track is built round: a star's (its ring and the gap outside it), or a bend in the open. `turn` 1 anticlockwise, -1 clockwise. */
export interface StreamCircle {
  c: Vec2;
  r: number;
  turn: 1 | -1;
  star?: SunDef;
}

/** What a piece of the track is, or what's in it. */
export type PieceKind = "sweep" | "glance" | "hairpin" | "bend" | "straight" | "narrows" | "gorge";

interface PieceBase {
  /** Where it starts along the track (blocks), and how long it is. */
  s0: number;
  len: number;
  name: string;
  kind: PieceKind;
}

/** An arc of a circle, from angle `a0`, `turn` the way round. */
export interface ArcPiece extends PieceBase {
  arc: true;
  c: Vec2;
  r: number;
  a0: number;
  turn: 1 | -1;
  /** The star it goes round, if it's a sweep or a glance. */
  star?: string;
}

/** A straight. */
export interface LinePiece extends PieceBase {
  arc: false;
  p0: Vec2;
  p1: Vec2;
  dir: Vec2;
}

export type StreamPiece = ArcPiece | LinePiece;

/** A named place on the track: where (s of its start, or its middle for a narrows or gorge) and what. */
export interface StreamPlace {
  s: number;
  len: number;
  name: string;
  kind: PieceKind;
  /** The star it's at, for a sweep or a glance. */
  star?: string;
}

/** A static rock lining the track, for the cell it falls in to make (see generateCell): a kerb on the outside of a bend, a wall of a narrows, a gorge's rock, or a lone rock to stand on. */
export interface BankSpec {
  at: Vec2;
  radius: number;
  kind: "kerb" | "wall" | "gorge" | "lone";
  seed: number;
  /** A gorge's channel (see AsteroidDef.channel). */
  channel?: { a: Vec2; b: Vec2; half: number };
}

export interface StreamRoute {
  /** The sphere it's the stream of (see sphereKey). */
  key: string;
  pieces: StreamPiece[];
  /** A lap, in blocks. */
  length: number;
  /** Every named place, in order along the track. */
  places: StreamPlace[];
  /** Which way the ring of each star on the route turns (1 anticlockwise), by sun id. */
  turns: Map<string, 1 | -1>;
  /** Where the band pinches (see bandWidth): s of the middle of each narrows. */
  narrows: number[];
  banks: BankSpec[];
  /** How many segments the belt is cut into, and how much belt each owns. */
  segments: number;
  segmentLength: number;
  /** The tightest bend's radius (how hard the inner edge of the band is squeezed, see streamSqueeze). */
  minRadius: number;
}

/** What a stream's global layer is keyed by: the same sphere makes the same track (see generateGlobal). */
export const sphereKey = (sphere: Pick<SphereConfig, "seed" | "radius" | "areaSize">): string => `${sphere.seed}:${sphere.radius}:${sphere.areaSize}`;

/** The sphere a key stands for (its id isn't part of generation). */
export function sphereOfKey(key: string): SphereConfig {
  const [seed, radius, areaSize] = key.split(":").map(Number);
  return { id: "world", seed: seed!, radius: radius!, areaSize: areaSize! };
}

const TAU = Math.PI * 2;
const mod = (a: number, m: number) => ((a % m) + m) % m;
const left = (u: Vec2): Vec2 => v2(-u.y, u.x);
const cross = (a: Vec2, b: Vec2) => a.x * b.y - a.y * b.x;
/** How close two stretches of track far apart along it may come (centre line to centre line): both corridors and a gap between. */
const SELF_CLEAR = STREAM.corridor * 2 + 200;

// ------------------------------------------------------------------ names

const ONSETS = ["", "b", "br", "c", "d", "dr", "f", "g", "gr", "h", "k", "kr", "l", "m", "n", "p", "r", "s", "st", "t", "th", "v", "vr", "w", "z"];
const VOWELS = ["a", "e", "i", "o", "u", "ae", "ia", "io", "ei", "ar", "or"];
const CODAS = ["", "n", "r", "l", "s", "th", "sh", "ck", "m", "rd", "st", "x", "v", "rn", "ll", "nd"];

/** A name from the seed ("Verrin", "Thiock"), unlike any in `taken`. */
export function makeName(rng: Rng, taken: Set<string>): string {
  for (let tries = 0; tries < 50; tries++) {
    const n = rng.next() < 0.6 ? 2 : 3;
    let w = "";
    for (let i = 0; i < n; i++) w += rng.pick(ONSETS) + rng.pick(VOWELS) + (i === n - 1 || rng.next() < 0.3 ? rng.pick(CODAS) : "");
    if (w.length < 3 || w.length > 9) continue;
    const name = w[0]!.toUpperCase() + w.slice(1);
    if (taken.has(name)) continue;
    taken.add(name);
    return name;
  }
  const name = `Star ${taken.size + 1}`;
  taken.add(name);
  return name;
}

// -------------------------------------------------------------- the track

/**
 * Lay out the track for a sphere: the stars (with `ringOuter`, how far
 * each one's ring reaches) are visited in a loop that twists through the
 * sphere, bends are added in the open between them, and the result is
 * checked (inside the shell, clear of every star's ring, never crossing
 * itself). Tried again with other rolls until it's good; the last resort
 * is the plain loop of the stars with no bends.
 */
export function makeStreamRoute(sphere: SphereConfig, suns: readonly SunDef[], ringOuter: (sun: SunDef) => number, taken: Set<string>): StreamRoute {
  const key = sphereKey(sphere);
  const limit = sphere.radius * 0.88 - STREAM.shellGap;
  const exclusion = (sun: SunDef) => ringOuter(sun) + STREAM.ringGap + STREAM.band / 2;
  const stars = suns.filter((s) => vLen(s.position) + exclusion(s) < limit);
  let best: { circles: StreamCircle[]; pieces: StreamPiece[] } | null = null;
  for (let attempt = 0; attempt < 60 && !best; attempt++) {
    const rng = new Rng((sphere.seed ^ 0x57e3a) + attempt * 7919);
    const tourCircles = tour(stars, exclusion, rng, limit, attempt < 40);
    if (!tourCircles) continue;
    const circles = attempt < 48 ? addBends(tourCircles, suns, exclusion, rng, limit) : tourCircles;
    const pieces = chain(circles);
    if (pieces && valid(pieces, circles, suns, exclusion, limit)) best = { circles, pieces };
  }
  if (!best) {
    // A sphere with no star in reach of the track: a plain loop round the middle.
    const r = Math.max(400, Math.min(limit - 100, sphere.radius * 0.35));
    const circles: StreamCircle[] = [{ c: v2(r * 0.5, 0), r: r * 0.5, turn: 1 }, { c: v2(-r * 0.5, 0), r: r * 0.5, turn: 1 }];
    best = { circles, pieces: chain(circles)! };
  }
  return finish(key, sphere, best.pieces, best.circles, taken);
}

/**
 * Which stars the track visits, in order, each as the circle it goes round:
 * every star, by a loop through them all that never crosses itself, where
 * one can be found (`all`, see everyStar); else some of them (see
 * someStars). One star makes a loop out to a far circle and back (a sweep
 * and a hairpin), or round it alone where there's no room for that; two, a
 * loop round both.
 */
function tour(stars: readonly SunDef[], exclusion: (sun: SunDef) => number, rng: Rng, limit: number, all: boolean): StreamCircle[] | null {
  if (!stars.length) return null;
  const circle = (star: SunDef, turn: 1 | -1): StreamCircle => ({ c: star.position, r: exclusion(star), turn, star });
  if (stars.length <= 2) {
    const [a, b] = stars;
    const first = circle(a!, 1);
    if (b) return [first, circle(b, 1)];
    // A far circle out toward the shell, as far as there's room for: a sweep round the star, a straight out, a hairpin and a straight back.
    for (let tries = 0; tries < 24; tries++) {
      // As big a far circle as fits: in a small sphere, down to not much more than half the band (the inside edge squeezed, see streamSqueeze).
      const r = STREAM.bend.radius[1] - (STREAM.bend.radius[1] - STREAM.band * 0.95) * Math.min(1, tries / 16);
      const ang = rng.range(0, TAU);
      const d = first.r + rng.range(1800, 2600);
      const far = v2(a!.position.x + Math.cos(ang) * d, a!.position.y + Math.sin(ang) * d);
      // Pulled in to fit the shell, if it must be, as long as the straights keep some length.
      const over = vLen(far) + r - limit;
      const pulled = over > 0 ? d - over : d;
      if (pulled < first.r + r + STREAM.corridor) continue;
      const c = v2(a!.position.x + Math.cos(ang) * pulled, a!.position.y + Math.sin(ang) * pulled);
      if (vLen(c) + r <= limit) return [first, { c, r, turn: 1 }];
    }
    // No room for that (a small sphere, a wide ring): once round the star, just outside its ring.
    return [first];
  }
  const picked = (all && everyStar(stars, exclusion, rng)) || someStars(stars, exclusion, rng);
  // Each circle is gone round the way the loop turns at it: anticlockwise at a convex corner of the loop, clockwise at a reflex one.
  const n = picked.length;
  const out: StreamCircle[] = [];
  for (let i = 0; i < n; i++) {
    const p = picked[(i + n - 1) % n]!.position;
    const q = picked[i]!.position;
    const r = picked[(i + 1) % n]!.position;
    const turn: 1 | -1 = cross(v2(q.x - p.x, q.y - p.y), v2(r.x - q.x, r.y - q.y)) >= 0 ? 1 : -1;
    out.push(circle(picked[i]!, turn));
  }
  // Neighbours whose circles meet can't be joined: no track this time.
  for (let i = 0; i < n; i++) {
    const a = out[i]!;
    const b = out[(i + 1) % n]!;
    if (vDist(a.c, b.c) < a.r + b.r + STREAM.corridor * 2) return null;
  }
  return out;
}

/**
 * Every star, in an order the track can go round them without crossing
 * itself, or null if none is found in a while: a randomised search, step by
 * step to a neighbouring star (near enough to be the next along, across a
 * dark reach if need be, with no other star's ring by the way between
 * them), the one with the fewest ways on first, and never across a stretch
 * already laid.
 */
function everyStar(stars: readonly SunDef[], exclusion: (sun: SunDef) => number, rng: Rng): SunDef[] | null {
  const n = stars.length;
  const pos = stars.map((s) => s.position);
  const nearest = Math.min(...pos.map((p, i) => Math.min(...pos.filter((_, j) => j !== i).map((q) => vDist(p, q)))));
  const nbrs: number[][] = pos.map(() => []);
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      // Out to three steps on the grid of stars: over a dark reach (a cell with no star) as well as to the stars beside.
      if (vDist(pos[i]!, pos[j]!) > nearest * 3.1) continue;
      // The track runs off to one side of the line between them by as much as the circle it's leaving or coming to (tapering from one to the other):
      // nothing else's ring may be that close.
      const ei = exclusion(stars[i]!);
      const ej = exclusion(stars[j]!);
      if (
        stars.some((s, k) => {
          if (k === i || k === j) return false;
          const { d, t } = segmentNear(pos[k]!, pos[i]!, pos[j]!);
          return d < exclusion(s) + ei * (1 - t) + ej * t;
        })
      )
        continue;
      nbrs[i]!.push(j);
      nbrs[j]!.push(i);
    }
  }
  if (nbrs.some((list) => list.length < 2)) return null;
  const path = [rng.int(0, n - 1)];
  const used = new Array<boolean>(n).fill(false);
  used[path[0]!] = true;
  let budget = 20_000;
  // Would the stretch from star a to star b cross one already laid (other than those it meets end to end)?
  const crosses = (a: number, b: number) => {
    for (let k = 0; k + 1 < path.length; k++) {
      const p = path[k]!;
      const q = path[k + 1]!;
      if (p === a || p === b || q === a || q === b) continue;
      if (segmentsCross(pos[a]!, pos[b]!, pos[p]!, pos[q]!)) return true;
    }
    return false;
  };
  const step = (): boolean => {
    if (--budget < 0) return false;
    const last = path[path.length - 1]!;
    if (path.length === n) return nbrs[last]!.includes(path[0]!) && !crosses(last, path[0]!);
    const options = nbrs[last]!
      .filter((j) => !used[j] && !crosses(last, j))
      .map((j) => ({ j, k: nbrs[j]!.filter((x) => !used[x]).length + rng.next() }))
      .sort((p, q) => p.k - q.k);
    for (const { j } of options) {
      used[j] = true;
      path.push(j);
      if (step()) return true;
      path.pop();
      used[j] = false;
    }
    return false;
  };
  return step() ? path.map((i) => stars[i]!) : null;
}

/** How far point p is from the segment a to b (`d`), and how far along it (0 at a, 1 at b) the nearest point is (`t`). */
function segmentNear(p: Vec2, a: Vec2, b: Vec2): { d: number; t: number } {
  const d = v2(b.x - a.x, b.y - a.y);
  const len2 = d.x * d.x + d.y * d.y;
  const t = len2 ? Math.max(0, Math.min(1, ((p.x - a.x) * d.x + (p.y - a.y) * d.y) / len2)) : 0;
  return { d: vDist(p, v2(a.x + d.x * t, a.y + d.y * t)), t };
}

/** Do segments a to b and c to d cross (touching ends don't count)? */
function segmentsCross(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
  const o = (p: Vec2, q: Vec2, r: Vec2) => Math.sign(cross(v2(q.x - p.x, q.y - p.y), v2(r.x - p.x, r.y - p.y)));
  return o(a, b, c) * o(a, b, d) < 0 && o(c, d, a) * o(c, d, b) < 0;
}

/**
 * Some of the stars, in order: sorted by angle round the sphere's middle
 * they make a loop that never crosses itself, and picking by turns from the
 * outer and inner ones makes it zigzag between the shell and the middle.
 */
function someStars(stars: readonly SunDef[], exclusion: (sun: SunDef) => number, rng: Rng): SunDef[] {
  const R = Math.max(...stars.map((s) => vLen(s.position)));
  const middle = stars.filter((s) => vLen(s.position) < 1);
  const round = stars
    .filter((s) => vLen(s.position) >= 1)
    .map((s) => ({ s, a: Math.atan2(s.position.y, s.position.x), far: vLen(s.position) > R * 0.5 }))
    .sort((p, q) => p.a - q.a);
  // Start somewhere round, so different attempts pick different stars.
  const start = rng.int(0, round.length - 1);
  const picked: SunDef[] = [];
  let wantFar = rng.next() < 0.5;
  for (let i = 0; i < round.length; i++) {
    const { s, far } = round[(start + i) % round.length]!;
    const take = far === wantFar ? rng.next() < 0.95 : rng.next() < 0.5;
    if (!take) continue;
    const prev = picked[picked.length - 1];
    if (prev && vDist(prev.position, s.position) < exclusion(prev) + exclusion(s) + SELF_CLEAR) continue;
    picked.push(s);
    if (rng.next() < 0.7) wantFar = !wantFar;
  }
  if (picked.length < 3) {
    for (const { s } of round) if (!picked.includes(s) && picked.length < 3) picked.push(s);
    picked.sort((p, q) => Math.atan2(p.position.y, p.position.x) - Math.atan2(q.position.y, q.position.x));
  }
  // Back through the middle now and then: the middle star goes between two of its neighbours.
  if (middle.length && rng.next() < 0.7) picked.splice(rng.int(1, picked.length - 1), 0, middle[0]!);
  return picked;
}

/**
 * Bend circles in the open between stars (see STREAM.bend): one or two on
 * each long enough gap, off to one side of the line between the stars (two
 * to opposite sides, for an S), gone round the far way so the track bulges
 * out round them. Only where there's room: inside the shell, and clear of
 * every star's ring.
 */
function addBends(circles: StreamCircle[], suns: readonly SunDef[], exclusion: (sun: SunDef) => number, rng: Rng, limit: number): StreamCircle[] {
  const B = STREAM.bend;
  const out: StreamCircle[] = [];
  const n = circles.length;
  for (let i = 0; i < n; i++) {
    const a = circles[i]!;
    const b = circles[(i + 1) % n]!;
    out.push(a);
    const d = v2(b.c.x - a.c.x, b.c.y - a.c.y);
    const D = vLen(d);
    // A long gap always gets bends; a shorter one, usually.
    if (D < B.room || (D < B.room * 1.8 && rng.next() >= B.chance)) continue;
    const u = v2(d.x / D, d.y / D);
    const l = left(u);
    const bends = D >= B.room * 3 ? 3 : D >= B.room * 1.6 && rng.next() < 0.5 ? 2 : 1;
    const ts = bends === 3 ? [rng.range(0.2, 0.3), rng.range(0.43, 0.57), rng.range(0.7, 0.8)] : bends === 2 ? [rng.range(0.27, 0.4), rng.range(0.6, 0.73)] : [rng.range(0.36, 0.64)];
    let side: 1 | -1 = rng.next() < 0.5 ? 1 : -1;
    for (const t of ts) {
      const r = rng.range(B.radius[0], B.radius[1]);
      const off = r * rng.range(B.offset[0], B.offset[1]);
      const c = v2(a.c.x + u.x * t * D + l.x * off * side, a.c.y + u.y * t * D + l.y * off * side);
      const fits = vLen(c) + r < limit && suns.every((s) => vDist(s.position, c) > exclusion(s) + r + STREAM.corridor * 2) && vDist(c, a.c) > a.r + r + STREAM.corridor * 2 && vDist(c, b.c) > b.r + r + STREAM.corridor * 2;
      // Displaced to the left of the way we're going, it's gone round clockwise (its far side), and the other way about.
      if (fits) out.push({ c, r, turn: side > 0 ? -1 : 1 });
      side = side > 0 ? -1 : 1;
    }
  }
  return out;
}

/**
 * The track through a loop of circles: the tangent line from each to the
 * next (an outer tangent between circles gone round the same way, a
 * crossing one between circles gone round opposite ways), and on each
 * circle the arc from where the line before touches to where the next one
 * leaves (one circle alone: all the way round it). Null if a pair of
 * circles can't be joined.
 */
function chain(circles: StreamCircle[]): StreamPiece[] | null {
  const n = circles.length;
  if (n === 1) {
    // Round one circle and back to the start: a single arc, all the way round.
    const C = circles[0]!;
    return [{ arc: true, c: C.c, r: C.r, a0: 0, turn: C.turn, s0: 0, len: TAU * C.r, name: "", kind: C.star ? "glance" : "bend", star: C.star?.id }];
  }
  if (n < 2) return null;
  const lines: { p0: Vec2; p1: Vec2 }[] = [];
  for (let i = 0; i < n; i++) {
    const P = circles[i]!;
    const Q = circles[(i + 1) % n]!;
    const d = v2(Q.c.x - P.c.x, Q.c.y - P.c.y);
    const D = vLen(d);
    if (D < 1e-6) return null;
    // The line's direction: the circles' edges, each on the side its turn puts it, must lie on it.
    const k = (Q.r * Q.turn - P.r * P.turn) / D;
    if (Math.abs(k) >= 1) return null;
    const psi = Math.atan2(d.y, d.x) - Math.asin(k);
    const u = v2(Math.cos(psi), Math.sin(psi));
    const l = left(u);
    lines.push({ p0: v2(P.c.x - P.r * P.turn * l.x, P.c.y - P.r * P.turn * l.y), p1: v2(Q.c.x - Q.r * Q.turn * l.x, Q.c.y - Q.r * Q.turn * l.y) });
  }
  const pieces: StreamPiece[] = [];
  for (let i = 0; i < n; i++) {
    const C = circles[i]!;
    const into = lines[(i + n - 1) % n]!.p1;
    const outOf = lines[i]!.p0;
    const a0 = Math.atan2(into.y - C.c.y, into.x - C.c.x);
    const a1 = Math.atan2(outOf.y - C.c.y, outOf.x - C.c.x);
    const da = mod(C.turn * (a1 - a0), TAU);
    if (da * C.r > 1) pieces.push({ arc: true, c: C.c, r: C.r, a0, turn: C.turn, s0: 0, len: da * C.r, name: "", kind: C.star ? "glance" : "bend", star: C.star?.id });
    const { p0, p1 } = lines[i]!;
    const len = vDist(p0, p1);
    if (len > 1) pieces.push({ arc: false, p0, p1, dir: v2((p1.x - p0.x) / len, (p1.y - p0.y) / len), s0: 0, len, name: "", kind: "straight" });
  }
  let s = 0;
  for (const p of pieces) {
    p.s0 = s;
    s += p.len;
  }
  return pieces;
}

/** Points along the track every `step` blocks (the last a step or less before the first again). */
export function routePolyline(route: Pick<StreamRoute, "pieces" | "length">, step: number): { p: Vec2; s: number }[] {
  const out: { p: Vec2; s: number }[] = [];
  for (let s = 0; s < route.length; s += step) out.push({ p: streamPoint(route, s).p, s });
  return out;
}

/**
 * Is the track good? Inside the shell everywhere; clear of every star's
 * ring but on the arc round that star; and never near itself (two stretches
 * closer than SELF_CLEAR, more than twice that apart along it).
 */
function valid(pieces: StreamPiece[], circles: StreamCircle[], suns: readonly SunDef[], exclusion: (sun: SunDef) => number, limit: number): boolean {
  const length = pieces.reduce((n, p) => n + p.len, 0);
  const route = { pieces, length };
  const step = 30;
  const samples = routePolyline(route, step);
  const clear = SELF_CLEAR;
  for (const { p, s } of samples) {
    if (vLen(p) > limit) return false;
    const piece = pieceAt(route, s);
    for (const sun of suns) {
      if (piece.arc && piece.star === sun.id) continue;
      if (vDist(sun.position, p) < exclusion(sun) - 1) return false;
    }
    // Not inside a bend circle it isn't going round (the arcs and their tangents touch the circle, nothing else comes near).
    for (const c of circles) {
      if (c.star || (piece.arc && piece.c === c.c)) continue;
      if (vDist(c.c, p) < c.r - 1 && !(piece.arc === false && lineTouches(piece, c))) return false;
    }
  }
  // Nearness to itself, through a grid of the samples.
  const cell = clear;
  const grid = new Map<string, number[]>();
  const keyOf = (p: Vec2) => `${Math.floor(p.x / cell)},${Math.floor(p.y / cell)}`;
  samples.forEach(({ p }, i) => {
    const k = keyOf(p);
    const list = grid.get(k);
    if (list) list.push(i);
    else grid.set(k, [i]);
  });
  for (let i = 0; i < samples.length; i++) {
    const { p, s } = samples[i]!;
    const cx = Math.floor(p.x / cell);
    const cy = Math.floor(p.y / cell);
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        for (const j of grid.get(`${cx + dx},${cy + dy}`) ?? []) {
          if (j <= i) continue;
          const q = samples[j]!;
          const along = Math.min(Math.abs(q.s - s), length - Math.abs(q.s - s));
          if (along > clear * 2 && vDist(p, q.p) < clear) return false;
        }
      }
    }
  }
  return true;
}

/** Does a straight touch a circle (it's the tangent into or out of it)? */
function lineTouches(line: LinePiece, c: StreamCircle): boolean {
  return Math.abs(vDist(line.p0, c.c) - c.r) < 1 || Math.abs(vDist(line.p1, c.c) - c.r) < 1;
}

/**
 * Finish a track: name its pieces (a sweep or a glance at each star by how
 * far round it goes, a hairpin or a bend in the open), put a narrows or a
 * gorge on some of the long straights, line it with banks, and cut it into
 * segments.
 */
function finish(key: string, sphere: SphereConfig, pieces: StreamPiece[], circles: StreamCircle[], taken: Set<string>): StreamRoute {
  const rng = new Rng(sphere.seed ^ 0x9a3e1);
  const length = pieces.reduce((n, p) => n + p.len, 0);
  const route: StreamRoute = { key, pieces, length, places: [], turns: new Map(), narrows: [], banks: [], segments: 0, segmentLength: 0, minRadius: Infinity };
  for (const c of circles) if (c.star) route.turns.set(c.star.id, c.turn);
  const starName = (id: string | undefined) => circles.find((c) => c.star?.id === id)?.star?.name ?? "a star";
  for (const p of pieces) {
    if (p.arc) {
      route.minRadius = Math.min(route.minRadius, p.r);
      const turns = p.len / (TAU * p.r);
      if (p.star) {
        p.kind = turns >= STREAM.sweepTurn ? "sweep" : "glance";
        p.name = `the ${p.kind === "sweep" ? "Sweep" : "Glance"} at ${starName(p.star)}`;
      } else {
        p.kind = turns >= STREAM.hairpinTurn ? "hairpin" : "bend";
        // A kink too slight to notice isn't a place.
        if (turns < 0.08) continue;
        p.name = `${makeName(rng, taken)} ${p.kind === "hairpin" ? "Hairpin" : "Bend"}`;
      }
      route.places.push({ s: p.s0, len: p.len, name: p.name, kind: p.kind, star: p.star });
    } else {
      p.name = `the ${makeName(rng, taken)} Straight`;
      route.places.push({ s: p.s0, len: p.len, name: p.name, kind: "straight" });
      const N = STREAM.narrows;
      const G = STREAM.gorge;
      const mid = p.s0 + p.len / 2;
      if (p.len >= G.minStraight && rng.next() < G.chance) {
        const name = `${makeName(rng, taken)} Gorge`;
        route.places.push({ s: mid, len: 0, name, kind: "gorge" });
        const radius = Math.round(rng.range(G.radius[0], G.radius[1]));
        const at = streamPoint(route, mid).p;
        const reach = radius * 1.25 + 30;
        route.banks.push({ at, radius, kind: "gorge", seed: rng.int(1, 0x7fffffff), channel: { a: v2(-p.dir.x * reach, -p.dir.y * reach), b: v2(p.dir.x * reach, p.dir.y * reach), half: STREAM.band / 2 + 20 } });
      } else if (p.len >= N.minStraight && rng.next() < N.chance) {
        route.places.push({ s: mid, len: N.length, name: `the ${makeName(rng, taken)} Narrows`, kind: "narrows" });
        route.narrows.push(mid);
      }
    }
  }
  if (!Number.isFinite(route.minRadius)) route.minRadius = STREAM.bend.radius[0];
  route.segments = Math.max(1, Math.round(length / STREAM.segment));
  route.segmentLength = length / route.segments;
  banks(route, rng, sphere, circles);
  return route;
}

/** The static rocks lining the track (see BankSpec), clear of the shell and of every circle the track goes round. */
function banks(route: StreamRoute, rng: Rng, sphere: SphereConfig, circles: StreamCircle[]): void {
  const limit = sphere.radius * 0.88 - 60;
  // Clear of the shell, and of every ring the track goes round: the bank's grid (a square, however the ring runs past it) past the ring rocks' grids.
  const fits = (at: Vec2, radius: number) => vLen(at) + radius * 1.25 < limit && circles.every((c) => !c.star || vDist(c.c, at) > c.r - STREAM.band / 2 - STREAM.ringGap + (radius * 1.25 + 20) * 1.42 + 50);
  const put = (at: Vec2, radius: number, kind: BankSpec["kind"]) => {
    if (fits(at, radius)) route.banks.push({ at: v2(Math.round(at.x), Math.round(at.y)), radius, kind, seed: rng.int(1, 0x7fffffff) });
  };
  const K = STREAM.kerbs;
  const W = STREAM.narrows;
  const L = STREAM.lone;
  for (const p of route.pieces) {
    if (p.arc) {
      // Kerbs on the outside of a real bend, spaced along it.
      if (p.len / (TAU * p.r) < 0.25) continue;
      const n = Math.floor((p.len * 0.8) / K.every);
      for (let i = 0; i < n; i++) {
        const s = p.s0 + p.len * 0.1 + (i + 0.5) * ((p.len * 0.8) / n);
        const radius = rng.int(K.radius[0], K.radius[1]);
        const { p: q, dir } = streamPoint(route, s);
        // Outside: away from the circle's middle, which is to the left going anticlockwise.
        const out = left(dir);
        const off = -p.turn * (STREAM.corridor + STREAM.bankGap + radius * 1.25);
        put(v2(q.x + out.x * off, q.y + out.y * off), radius, "kerb");
      }
    } else {
      const mids = route.places.filter((pl) => (pl.kind === "narrows" || pl.kind === "gorge") && pl.s > p.s0 && pl.s < p.s0 + p.len);
      for (const m of mids) {
        if (m.kind !== "narrows") continue;
        // Two walls along the pinch.
        for (let s = m.s - W.length / 2; s <= m.s + W.length / 2; s += 2.6 * W.wall[1]) {
          for (const side of [1, -1]) {
            const radius = rng.int(W.wall[0], W.wall[1]);
            const { p: q, dir } = streamPoint(route, s);
            const l = left(dir);
            const off = side * (STREAM.corridor + radius * 1.25);
            put(v2(q.x + l.x * off, q.y + l.y * off), radius, "wall");
          }
        }
      }
      // Lone rocks to stand on, either side by turns, away from the ends and from whatever's in the middle.
      let side = rng.next() < 0.5 ? 1 : -1;
      for (let s = p.s0 + L.every * 0.6; s < p.s0 + p.len - 200; s += L.every * rng.range(0.8, 1.3)) {
        if (mids.some((m) => Math.abs(m.s - s) < 420)) continue;
        const radius = rng.int(L.radius[0], L.radius[1]);
        const { p: q, dir } = streamPoint(route, s);
        const l = left(dir);
        const off = side * (STREAM.corridor + STREAM.bankGap + radius * 1.25 + rng.range(0, 40));
        put(v2(q.x + l.x * off, q.y + l.y * off), radius, "lone");
        side = -side as 1 | -1;
      }
    }
  }
}

// -------------------------------------------------------------- geometry

/** The piece of the track `s` blocks along (s wrapped to a lap). */
export function pieceAt(route: Pick<StreamRoute, "pieces" | "length">, s: number): StreamPiece {
  const u = mod(s, route.length);
  const pieces = route.pieces;
  let lo = 0;
  let hi = pieces.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (pieces[mid]!.s0 <= u) lo = mid;
    else hi = mid - 1;
  }
  return pieces[lo]!;
}

/** Where the track's centre line is `s` blocks along, and which way it goes there. */
export function streamPoint(route: Pick<StreamRoute, "pieces" | "length">, s: number): { p: Vec2; dir: Vec2; piece: StreamPiece } {
  const u = mod(s, route.length);
  const piece = pieceAt(route, u);
  const t = u - piece.s0;
  if (piece.arc) {
    const a = piece.a0 + (piece.turn * t) / piece.r;
    return { p: v2(piece.c.x + Math.cos(a) * piece.r, piece.c.y + Math.sin(a) * piece.r), dir: v2(-Math.sin(a) * piece.turn, Math.cos(a) * piece.turn), piece };
  }
  return { p: v2(piece.p0.x + piece.dir.x * t, piece.p0.y + piece.dir.y * t), dir: piece.dir, piece };
}

/** The nearest point of one piece to `p`: how far along the track, how far across (left of the way it goes positive), and the distance. */
function nearestOn(piece: StreamPiece, p: Vec2): { s: number; o: number; d: number } {
  if (piece.arc) {
    const dx = p.x - piece.c.x;
    const dy = p.y - piece.c.y;
    const phi = Math.atan2(dy, dx);
    const da = piece.len / piece.r;
    const delta = mod(piece.turn * (phi - piece.a0), TAU);
    if (delta <= da) {
      const q = v2(piece.c.x + Math.cos(phi) * piece.r, piece.c.y + Math.sin(phi) * piece.r);
      const dir = v2(-Math.sin(phi) * piece.turn, Math.cos(phi) * piece.turn);
      const d = vDist(p, q);
      return { s: piece.s0 + delta * piece.r, o: cross(dir, v2(p.x - q.x, p.y - q.y)), d };
    }
    // Past either end: the nearer end.
    const a1 = piece.a0 + piece.turn * da;
    const e0 = v2(piece.c.x + Math.cos(piece.a0) * piece.r, piece.c.y + Math.sin(piece.a0) * piece.r);
    const e1 = v2(piece.c.x + Math.cos(a1) * piece.r, piece.c.y + Math.sin(a1) * piece.r);
    const d0 = vDist(p, e0);
    const d1 = vDist(p, e1);
    const [q, s, a] = d0 <= d1 ? [e0, piece.s0, piece.a0] : [e1, piece.s0 + piece.len, a1];
    const dir = v2(-Math.sin(a) * piece.turn, Math.cos(a) * piece.turn);
    return { s, o: cross(dir, v2(p.x - q.x, p.y - q.y)), d: Math.min(d0, d1) };
  }
  const t = clamp((p.x - piece.p0.x) * piece.dir.x + (p.y - piece.p0.y) * piece.dir.y, 0, piece.len);
  const q = v2(piece.p0.x + piece.dir.x * t, piece.p0.y + piece.dir.y * t);
  return { s: piece.s0 + t, o: cross(piece.dir, v2(p.x - q.x, p.y - q.y)), d: vDist(p, q) };
}

/** How far a piece's nearest point can be from `p`, at least (a box round the piece). */
function atLeast(piece: StreamPiece, p: Vec2): number {
  const [x0, y0, x1, y1] = piece.arc ? [piece.c.x - piece.r, piece.c.y - piece.r, piece.c.x + piece.r, piece.c.y + piece.r] : [Math.min(piece.p0.x, piece.p1.x), Math.min(piece.p0.y, piece.p1.y), Math.max(piece.p0.x, piece.p1.x), Math.max(piece.p0.y, piece.p1.y)];
  return Math.hypot(Math.max(x0 - p.x, 0, p.x - x1), Math.max(y0 - p.y, 0, p.y - y1));
}

/** Where a world point is in the stream's frame: `s` blocks along the track (the nearest point of its centre line), `o` across it (left of the way it goes positive), and `d` its distance from the line. */
export function streamCoord(route: Pick<StreamRoute, "pieces" | "length">, p: Vec2): { s: number; o: number; d: number } {
  let best = { s: 0, o: 0, d: Infinity };
  for (const piece of route.pieces) {
    if (atLeast(piece, p) >= best.d) continue;
    const near = nearestOn(piece, p);
    if (near.d < best.d) best = near;
  }
  return best;
}

/** How far a world point is from the track's centre line (Infinity with no stream). What danger and riches go by (see stream_plan.md). */
export function streamDistance(route: Pick<StreamRoute, "pieces" | "length"> | undefined, p: Vec2): number {
  return route ? streamCoord(route, p).d : Infinity;
}

/** How wide the band is `s` blocks along: STREAM.band, pinched at a narrows (see STREAM.narrows). */
export function bandWidth(route: Pick<StreamRoute, "narrows" | "length">, s: number): number {
  const N = STREAM.narrows;
  let w: number = STREAM.band;
  for (const m of route.narrows) {
    const ds = Math.abs(mod(s - m + route.length / 2, route.length) - route.length / 2);
    if (ds <= N.length / 2) w = Math.min(w, N.width);
    else if (ds <= N.length) {
      const k = (ds - N.length / 2) / (N.length / 2);
      w = Math.min(w, N.width + (STREAM.band - N.width) * k * k * (3 - 2 * k));
    }
  }
  return w;
}

/** How much the belt is pushed together along the track on the inside of its tightest bend, for a rock `o` blocks across the band: 1 mid-band, less toward the inside edge. */
export function streamSqueeze(route: Pick<StreamRoute, "minRadius">, o: number): number {
  return (route.minRadius - Math.abs(o)) / route.minRadius;
}

/** Which segment of the belt a belt coordinate is in (see STREAM.segment). */
export function segmentOf(route: Pick<StreamRoute, "segments" | "segmentLength" | "length">, u: number): number {
  return Math.min(route.segments - 1, Math.floor(mod(u, route.length) / route.segmentLength));
}

/** Where on the track a rock at belt coordinate `u` is at time `t` (s). */
export function streamAt(route: Pick<StreamRoute, "pieces" | "length">, u: number, t: number): { p: Vec2; dir: Vec2; piece: StreamPiece; s: number } {
  const s = mod(u + STREAM.speed * t, route.length);
  return { ...streamPoint(route, s), s };
}

/** The places ahead of `s` along the track, nearest first, each with how far on it is (blocks). */
export function placesAhead(route: Pick<StreamRoute, "places" | "length">, s: number): { place: StreamPlace; ahead: number }[] {
  return route.places
    .map((place) => ({ place, ahead: mod(place.s - s, route.length) }))
    .sort((a, b) => a.ahead - b.ahead);
}

/** The next star along the track from `s` (the start of the arc round it), and how far on it is. */
export function nextStar(route: Pick<StreamRoute, "places" | "length">, s: number): { place: StreamPlace; ahead: number } | undefined {
  return placesAhead(route, s).find(({ place }) => place.star);
}

/** The place `s` is in (an arc, a straight, or the narrows or gorge in a straight). */
export function placeAt(route: Pick<StreamRoute, "places" | "length">, s: number): StreamPlace | undefined {
  const u = mod(s, route.length);
  const feature = route.places.find((p) => (p.kind === "narrows" || p.kind === "gorge") && Math.abs(p.s - u) <= Math.max(p.len, 200) / 2);
  if (feature) return feature;
  let best: StreamPlace | undefined;
  for (const p of route.places) if (p.len > 0 && p.s <= u && (!best || p.s > best.s)) best = p;
  return best;
}
